import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import images from "../../../utilities/images/images";
import "./AitsTestInstruction.css";
import "./BitsatTestInstruction.css";

import HeaderInstruction from "../components/Instruction/HeaderInstruction";
import { setTestDetails } from "../../../redux/exam/examSlice";
import { resolveYearWiseReturnPath } from "../../../custom_utilities/dashboardBannerNavigation";
import { getStartOrResumeTestLabel } from "../../StudyDashboard/utils/resumeTestUtils";

const QUIZMASTER_ORIGIN = "https://quizmaster.motion.ac.in";

const resolveInstructionAssetUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  let url = value.trim().replace(/&amp;/g, "&");
  if (!url || /^(null|undefined|#)$/i.test(url)) return "";

  url = url.replace(
    /^https?:\/\/(?:quizmaster|quil-master)\.motion\.ac\.in/i,
    QUIZMASTER_ORIGIN
  );

  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("//")) return `https:${url}`;
  if (url.startsWith("/")) return `${QUIZMASTER_ORIGIN}${url}`;
  if (/^ckfinder\//i.test(url)) return `${QUIZMASTER_ORIGIN}/${url}`;
  return `${QUIZMASTER_ORIGIN}/${url.replace(/^\.\//, "")}`;
};

const expandAssetUrls = (value) => {
  const primary = resolveInstructionAssetUrl(value);
  if (!primary) return [];

  const urls = [primary];
  if (/^https:\/\/quizmaster\.motion\.ac\.in/i.test(primary)) {
    urls.push(primary.replace(/^https:/i, "http:"));
  }
  if (/\/ckfinder\//i.test(primary)) {
    const learningUrl = primary.replace(
      /^https?:\/\/[^/]+/i,
      "https://learning.motion.ac.in"
    );
    if (!urls.includes(learningUrl)) urls.push(learningUrl);
  }
  return urls;
};

const uniqueUrls = (urls) => {
  const seen = new Set();
  return urls.filter((url) => {
    if (!url || seen.has(url)) return false;
    seen.add(url);
    return true;
  });
};

const visibleInstructionText = (html) =>
  String(html || "")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();

const isUsableInstructionHtml = (html) => {
  const text = visibleInstructionText(html).replace(
    /no\s*image\s*available/gi,
    ""
  );
  return text.trim().length > 0;
};

const PLACEHOLDER_IMAGE_LABEL =
  /no\s*image(?:\s*available)?|image\s*not\s*available|no\s*preview|placeholder/i;

const isPlaceholderImageSource = (src = "", alt = "") =>
  PLACEHOLDER_IMAGE_LABEL.test(`${alt} ${src}`);

const readImageTag = (tag) => ({
  src: (tag.match(/\bsrc\s*=\s*(["'])([\s\S]*?)\1/i) || [])[2] || "",
  alt: (tag.match(/\balt\s*=\s*(["'])([\s\S]*?)\1/i) || [])[2] || "",
});

const collectImageTags = (html) => {
  const tags = [];
  const re = /<img\b[^>]*>/gi;
  let match = re.exec(html || "");
  while (match) {
    const tag = match[0];
    const { src, alt } = readImageTag(tag);
    tags.push({
      tag,
      src,
      placeholderAlt: isPlaceholderImageSource(src, alt),
    });
    match = re.exec(html || "");
  }
  return tags;
};

const getInstructionImageCandidates = (inst) => {
  const tags = collectImageTags(
    `${inst?.test_instruction_eng || ""} ${inst?.instructions_html || ""}`
  );
  const preferred = tags
    .filter((tag) => !tag.placeholderAlt)
    .map((tag) => tag.src);

  return uniqueUrls(
    [inst?.instructions_image, ...preferred].flatMap(expandAssetUrls)
  );
};

const stripEmptyInstructionBlocks = (html) => {
  let next = html;
  for (let pass = 0; pass < 3; pass += 1) {
    next = next.replace(
      /<(p|div|span|figure|center|h[1-6])\b[^>]*>\s*(?:<br\s*\/?>|&nbsp;|\s)*<\/\1>/gi,
      ""
    );
  }
  return next;
};

const rewriteInstructionHtml = (html) => {
  if (!html) return "";

  const withoutPlaceholderImages = html.replace(/<img\b[^>]*>/gi, (tag) => {
    const srcMatch = tag.match(/\bsrc\s*=\s*(["'])([\s\S]*?)\1/i);
    const { src, alt } = readImageTag(tag);
    if (!srcMatch || isPlaceholderImageSource(src, alt)) return "";

    const resolved = resolveInstructionAssetUrl(src);
    if (!resolved || isPlaceholderImageSource(resolved, alt)) return "";

    return tag.replace(srcMatch[0], `src=${srcMatch[1]}${resolved}${srcMatch[1]}`);
  });

  const withoutPlaceholderText = withoutPlaceholderImages.replace(
    /<(p|div|span|figure|center|h[1-6])\b[^>]*>\s*(?:<[^>]+>\s*)*no\s*image\s*available\s*(?:<\/[^>]+>\s*)*<\/\1>/gi,
    ""
  );

  return stripEmptyInstructionBlocks(withoutPlaceholderText);
};

const isBitsatInstruction = (inst) =>
  /bitsat/i.test(
    `${inst?.paper_name || ""} ${inst?.test_name || ""} ${inst?.paper_type || ""} ${inst?.target || ""}`
  );

const applyBitsatMarkingScheme = (html) =>
  html
    .replace(
      /1\s*marks?\s+for\s+every\s+correct\s+answer\.?/gi,
      "3 marks for every correct answer."
    )
    .replace(
      /no\s+negative\s+marking\.?/gi,
      "1 mark will be deducted for every incorrect answer."
    );

const getInstructionHtml = (inst) => {
  const source = [
    inst?.instructions_html,
    inst?.test_instruction_eng,
    inst?.test_instruction_hindi,
  ].find(isUsableInstructionHtml);

  if (!source) return "";
  const html = rewriteInstructionHtml(source);
  return isBitsatInstruction(inst) ? applyBitsatMarkingScheme(html) : html;
};

const looksLikeMissingImageGraphic = (img) => {
  if (isPlaceholderImageSource(img?.currentSrc || img?.src, img?.alt || "")) {
    return true;
  }

  try {
    const size = 40;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    context.drawImage(img, 0, 0, size, size);
    const { data } = context.getImageData(0, 0, size, size);
    let darkPixels = 0;
    let luminance = 0;
    const pixelCount = size * size;

    for (let index = 0; index < data.length; index += 4) {
      const level = (data[index] * 54 + data[index + 1] * 183 + data[index + 2] * 19) >> 8;
      luminance += level;
      if (level < 80) darkPixels += 1;
    }

    // Placeholder cards are pale and have almost no dark text.
    return luminance / pixelCount > 165 && darkPixels / pixelCount < 0.04;
  } catch (error) {
    return false;
  }
};

const buildFallbackInstructions = (inst) => {
  const name = inst?.paper_name || "This test";
  const questions = Number(inst?.total_questions) || null;
  const duration = Number(inst?.duration_mins || inst?.duration_time) || null;
  const marks = Number(inst?.total_marks || inst?.max_marks) || null;
  const isBitsat = /bitsat/i.test(
    `${name} ${inst?.paper_type || ""} ${inst?.target || ""}`
  );

  const facts = [];
  if (questions) facts.push(`${questions} questions`);
  if (marks) facts.push(`${marks} marks`);
  if (duration) facts.push(`${duration} minutes`);

  return [
    isBitsat
      ? `${name} is a computer-based test. Read each question and select one option.`
      : `${name} is a computer-based test. Read each question carefully before selecting an answer.`,
    facts.length
      ? `This paper has ${facts.join(", ")}. The timer starts when you begin.`
      : "The timer starts when you begin the test.",
    "Use Previous and Save & Next to move between questions, or open a question from the palette.",
    "Mark a question for review if you want to come back to it before submitting.",
    "Submit only after you have checked your responses. All the best.",
  ];
};

function InstructionImage({ candidates, onExhausted }) {
  const [index, setIndex] = useState(0);
  const candidateKey = candidates.join("|");

  useEffect(() => {
    setIndex(0);
  }, [candidateKey]);

  const src = candidates[index];
  if (!src) return null;

  return (
    <img
      src={src}
      alt=""
      onLoad={(event) => {
        if (looksLikeMissingImageGraphic(event.currentTarget)) onExhausted();
      }}
      onError={() => {
        if (index + 1 < candidates.length) {
          setIndex((current) => current + 1);
          return;
        }
        onExhausted();
      }}
    />
  );
}

function InstructionBody({ inst }) {
  const htmlRef = useRef(null);
  const imageCandidates = useMemo(
    () => getInstructionImageCandidates(inst),
    [inst]
  );
  const instructionHtml = useMemo(() => getInstructionHtml(inst), [inst]);
  const [imageExhausted, setImageExhausted] = useState(false);
  const candidateKey = imageCandidates.join("|");

  useEffect(() => {
    setImageExhausted(false);
  }, [candidateKey, instructionHtml]);

  useEffect(() => {
    const root = htmlRef.current;
    if (!root) return undefined;

    const removeImage = (img) => {
      const wrapper = img.parentElement;
      img.remove();
      if (
        wrapper &&
        wrapper !== root &&
        !visibleInstructionText(wrapper.innerHTML)
      ) {
        wrapper.remove();
      }
    };

    root.querySelectorAll("p, div, span, li, center, h1, h2, h3, h4").forEach((node) => {
      const text = (node.textContent || "").replace(/\s+/g, " ").trim();
      if (/^no\s*image\s*available\.?$/i.test(text)) node.remove();
    });

    const imgs = Array.from(root.querySelectorAll("img"));
    const unsubs = imgs.map((img) => {
      const onError = () => removeImage(img);
      const onLoad = () => {
        if (looksLikeMissingImageGraphic(img)) removeImage(img);
      };

      img.addEventListener("error", onError);
      img.addEventListener("load", onLoad);
      if (img.complete) {
        if (img.naturalWidth === 0) onError();
        else onLoad();
      }
      return () => {
        img.removeEventListener("error", onError);
        img.removeEventListener("load", onLoad);
      };
    });

    return () => unsubs.forEach((unsubscribe) => unsubscribe());
  }, [instructionHtml]);

  const showStandaloneImage =
    !instructionHtml && imageCandidates.length > 0 && !imageExhausted;
  const showFallback = !instructionHtml && !showStandaloneImage;

  return (
    <div className="general_content instructions_html_container">
      {showStandaloneImage ? (
        <InstructionImage
          candidates={imageCandidates}
          onExhausted={() => setImageExhausted(true)}
        />
      ) : null}
      {instructionHtml ? (
        <div
          ref={htmlRef}
          dangerouslySetInnerHTML={{ __html: instructionHtml }}
        />
      ) : null}
      {showFallback ? (
        <ul className="instruction_fallback">
          {buildFallbackInstructions(inst).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function BitsatTestInstruction() {
  const history = useHistory();
  const dispatch = useDispatch();
  const { instructions, testDetails } = useSelector((state) => state.examSlice);
  const source = testDetails?.source;
const selectedMode = testDetails?.practiceMode;
  const inst = instructions?.instruction || instructions;
  const attempt_text = inst?.attempt_text;
  const durationValue = inst?.duration_time || inst?.duration_mins;
  const hideDuration = !durationValue;
  const instructionResume =
    inst?.is_resume === true || Number(inst?.is_resume) === 1;
  const startingFresh = testDetails?.forceFreshStart === true;
  const startButtonLabel = getStartOrResumeTestLabel({
    forceFreshStart: startingFresh,
    isResume:
      testDetails?.isResume === false ? false : instructionResume,
  });

  const handleBeginOrResume = () => {
    // Reattempt / Start sets forceFreshStart; don't let backend is_resume override it.
    const startingFresh = testDetails?.forceFreshStart === true;

    dispatch(
      setTestDetails({
        ...testDetails,
        isResume: startingFresh ? false : instructionResume,
        forceFreshStart: startingFresh
          ? true
          : instructionResume
            ? false
            : testDetails?.forceFreshStart === true,
      })
    );

    if (source === "topicwise" || source === "mistake_book_practice_set") {
      history.push("/test", {
        paperType: inst?.paper_type ?? testDetails?.paperType,
        setId: inst?.set_id ?? testDetails?.setId,
      });
    } else {
      history.push("/test", {
        paperId: inst?.paper_id ?? testDetails?.paperId,
        paperType: inst?.paper_type ?? testDetails?.paperType,
        testId: inst?.test_id ?? testDetails?.testId,
      });
    }
  };
  return (
    <div className="aitstest_instruction">
      <div className="test_container">
        <img
          src={images.new_backarrow}
          alt="back"
          className="arrow_image"
          // onClick={() => history.push("/practice/paper-listing")}
          onClick={() => {
  if (source === "mistake_book_practice_set") {
    history.push("/practice/mistake-book/practice-sets");
  } else if (source === "topicwise") {
    history.push("/practice/topic-wise/pyqset");
  } else if (source === "yearwise") {
    history.push(resolveYearWiseReturnPath({ testDetails }), {
      year: testDetails?.year,
    });
  } else {
    history.push("/practice/paper-listing");
  }
}}

        />
        <span>Test Instruction</span>
      </div>

      <div className="common_padding">
        <HeaderInstruction
          name={inst?.paper_name}
          type={inst?.paper_type}
          duration={inst?.duration_mins}
          totalQuestions={inst?.total_questions}
          maxMarks={inst?.max_marks}
          date={inst?.start_date}
          instruction_image={`https://learning.motion.ac.in${inst?.instructions_image}`}
          paper_icon={inst?.paper_icon}
          duration_icon={inst?.duration_icon}
          question_icon={inst?.question_icon}
           hideDuration={hideDuration}
        />
        {attempt_text && selectedMode === "test" && (
  <div className="reattemptInfoContainer">
    <div className="reattemptRow">
      <span className="reattemptInfo">
        Attempts used:
      </span>

      <span className="attemptBadge">
        {attempt_text?.attempt_count}/
        {attempt_text?.total_attempt_count}
      </span>
    </div>

    <div className="reattemptSubtext">
      {attempt_text?.attempt_message}
    </div>
  </div>
)}

        {inst ? (
          <div className="general_wrapper">
            <div className="general_header">General Instructions</div>
            <InstructionBody inst={inst} />
          </div>
        ) : null}

        <div className="start_btn">
          <button
            className="back-tests-btn"
            // onClick={() => history.push("/paper-listing")}
            onClick={() => {
  if (source === "mistake_book_practice_set") {
    history.push("/practice/mistake-book/practice-sets");
  } else if (source === "topicwise") {
    history.push("/practice/topic-wise/pyqset");
  } else if (source === "yearwise") {
    history.push(resolveYearWiseReturnPath({ testDetails }), {
      year: testDetails?.year,
    });
  } else {
    history.push("/practice/paper-listing");
  }
}}
          >
            Back
          </button>

          <button
            className="begin-test-btn"
            onClick={handleBeginOrResume}
          >
            <span>
              {startButtonLabel}
            </span>
            <img
              src={images.rightArrow_icon}
              alt="arrow"
              className="arrow-icon"
            />
          </button>
        </div>
      </div>
    </div>
  );
}

export default BitsatTestInstruction;
